import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SubPageHeader from '@/components/layout/SubPageHeader';
import SubPageShell from '@/components/layout/SubPageShell';
import SubPageSection from '@/components/layout/SubPageSection';
import SubPageSurface from '@/components/layout/SubPageSurface';
import SectionHeading from '@/components/base/SectionHeading';
import { SUBPAGE_SURFACE_BORDER, SUBPAGE_SURFACE_HOVER } from '@/constants/surfaceColors';

const publicWorks = [
  '旧柏崎小学校跡地広場トイレ',
  '旧柏崎小学校跡地広場東屋',
  '中央卸売市場花き棟保冷庫',
  '小舟渡集会所',
  '八戸警察署階上交番',
  '八戸警察署中央交番',
  '八戸警察署大館駐在所',
  'むつ市営住宅川内榀木団地',
  '八戸市是川公民館',
  'おいらせ阿光坊古墳館',
  '八戸市蕪島休憩案内施設',
  '八戸防災ステーション',
  '八戸消防署南郷分遣所',
  '三戸消防署名川分遣所',
  '八戸工業高等学校第一体育館',
  '多賀多目的運動場天然芝球技場スタジアム照明',
  '世増ダム公園施設 公衆トイレ・展望台・東屋',
];

const privateWorks = [
  '特別養護老人ホームほっとハウス増築',
  '小規模多機能ホームほっとハウス',
  '認定こども園七色のみち',
  '中居林ふたば園',
  'グループホーム南ヶ丘',
  'グループホームまきば',
  '地域サポートステーションのぎく園',
  'グッジョブ妙光園',
  'シャイニー妙光園',
  '共同物流サービス新物流センター',
  '共同電算センター新社屋',
  '日成電設新社屋',
  'はちのへワイナリー',
  '青森トヨペット八戸本店',
  '薬王堂尻内店',
  '高周波鋳造事務所棟・工場棟',
  'アルバック東北事務所棟・工場棟',
  '八戸西高等学校生徒会館',
  '八戸東高等学校多目的会館',
  '八戸聖ウルスラ学院中・高等学校第一体育館',
];

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function WorksPage() {
  return (
    <SubPageShell>
      <Navbar />

      <SubPageHeader>
          <SectionHeading label="WORKS" title="実績" as="h1" tone="dark" size="page" />
          <p
            className="mt-4"
            style={{ fontSize: '17px', color: '#FFFFFF', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '1.9' }}
          >
            公共施設から民間施設まで、青森・八戸を中心に多数の実績を積み重ねてきました。
          </p>
      </SubPageHeader>

      {/* Public Works */}
      <SubPageSection>
          <FadeIn>
            <SectionHeading label="PUBLIC" title="公共施設" tone="dark" size="subsection" className="mb-14" />
          </FadeIn>

          <SubPageSurface>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0" style={{ border: `1px solid ${SUBPAGE_SURFACE_BORDER}` }}>
            {publicWorks.map((work, i) => (
              <FadeIn key={work} delay={Math.min(i * 30, 300)}>
                <div
                  className="flex items-start gap-5 px-6 py-5 group cursor-default transition-colors duration-200"
                  style={{
                    borderBottom: `1px solid ${SUBPAGE_SURFACE_BORDER}`,
                    borderRight: i % 2 === 0 ? `1px solid ${SUBPAGE_SURFACE_BORDER}` : 'none',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = SUBPAGE_SURFACE_HOVER; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span
                    className="flex-shrink-0 mt-1"
                    style={{ fontSize: '14px', color: '#CCCCCC', fontFamily: '"Inter", sans-serif', minWidth: '24px' }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span
                    style={{ fontSize: '15px', color: '#333333', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '1.8' }}
                  >
                    {work}
                  </span>
                </div>
              </FadeIn>
            ))}
            <FadeIn delay={300}>
              <div
                className="flex items-center gap-5 px-6 py-5"
                style={{ borderBottom: `1px solid ${SUBPAGE_SURFACE_BORDER}` }}
              >
                <span style={{ fontSize: '15px', color: '#AAAAAA', fontFamily: '"Noto Sans JP", sans-serif' }}>etc.</span>
              </div>
            </FadeIn>
          </div>
          </SubPageSurface>
      </SubPageSection>

      {/* Private Works */}
      <SubPageSection>
          <FadeIn>
            <SectionHeading label="PRIVATE" title="民間施設" tone="dark" size="subsection" className="mb-14" />
          </FadeIn>

          <SubPageSurface>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0" style={{ border: `1px solid ${SUBPAGE_SURFACE_BORDER}` }}>
            {privateWorks.map((work, i) => (
              <FadeIn key={work} delay={Math.min(i * 40, 300)}>
                <div
                  className="flex items-start gap-5 px-6 py-5 cursor-default transition-colors duration-200"
                  style={{
                    borderBottom: `1px solid ${SUBPAGE_SURFACE_BORDER}`,
                    borderRight: i % 2 === 0 ? `1px solid ${SUBPAGE_SURFACE_BORDER}` : 'none',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = SUBPAGE_SURFACE_HOVER; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span
                    className="flex-shrink-0 mt-1"
                    style={{ fontSize: '14px', color: '#CCCCCC', fontFamily: '"Inter", sans-serif', minWidth: '24px' }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span
                    style={{ fontSize: '15px', color: '#333333', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '1.8' }}
                  >
                    {work}
                  </span>
                </div>
              </FadeIn>
            ))}
            <FadeIn delay={300}>
              <div className="flex items-center gap-5 px-6 py-5" style={{ borderBottom: `1px solid ${SUBPAGE_SURFACE_BORDER}` }}>
                <span style={{ fontSize: '15px', color: '#AAAAAA', fontFamily: '"Noto Sans JP", sans-serif' }}>etc.</span>
              </div>
            </FadeIn>
          </div>
          </SubPageSurface>
      </SubPageSection>

      <Footer />
    </SubPageShell>
  );
}
